import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import {
  statusLabel,
  statusTone,
  therapyPhysicianSignatureRequired,
  THERAPY_DISCIPLINES,
  THERAPY_NOTE_TYPES,
  whyCannotCosign,
  whyCannotSign,
} from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { WorkspaceBack } from "@/components/app/workspace/workspace-back";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { usePolicy } from "@/lib/policy";
import {
  ENTIRE_NOTE,
  effectiveView,
  noteSections,
  type NoteView,
} from "@/components/app/therapy/note-sections";
import type { NoteValues } from "./-components/therapy/therapy-note-form";
import { CosignDialog } from "./-components/therapy/therapy-cosign-dialog";
import { SignDialog } from "./-components/therapy/therapy-sign-dialog";
import {
  label,
  ATTESTATION,
  PHYSICIAN_NOTE_TYPES,
} from "@/routes/_authenticated/_care/residents/$residentId/-lib/therapy-note-page";
import { useTherapyNoteActions } from "@/routes/_authenticated/_care/residents/$residentId/-hooks/use-therapy-note-actions";
import { NoteHeaderActions } from "./-components/therapy/therapy-note-header-actions";
import { NotePanels } from "./-components/therapy/therapy-note-panels";

export const Route = createFileRoute(
  "/_authenticated/_care/residents/$residentId/therapy-notes/$noteId",
)({
  component: TherapyNotePage,
});

function TherapyNotePage() {
  const { residentId, noteId } = Route.useParams();
  const { me, subject, can } = usePolicy();
  const [signing, setSigning] = useState<NoteValues | null>(null);
  const [cosigning, setCosigning] = useState(false);
  const [clearingSigned, setClearingSigned] = useState(false);
  // Lives here, not in the form: the rail also covers interventions, signatures and the audit
  // trail.
  const [view, setView] = useState<NoteView>(ENTIRE_NOTE);

  const note = useQuery(orpc.care.therapy.note.queryOptions({ input: { noteId } }));
  const tracks = useQuery(orpc.care.therapy.tracks.queryOptions({ input: { residentId } }));
  const requests = useQuery(
    orpc.care.residents.physicianSignatureRequests.list.queryOptions({ input: { residentId } }),
  );

  const episodeId = note.data?.episodeId;
  const track = tracks.data?.find((row) => row.episodes.some((one) => one.id === episodeId));
  const episode = track?.episodes.find((one) => one.id === episodeId);
  const codes = useQuery({
    ...orpc.care.therapy.cptCodes.queryOptions({
      input: { facilityId: track?.facilityId ?? "all", discipline: track?.discipline },
    }),
    enabled: Boolean(track),
  });

  const actions = useTherapyNoteActions({
    noteId,
    onSettled: () => {
      setSigning(null);
      setCosigning(false);
    },
  });

  const signedPageInput = useRef<HTMLInputElement>(null);

  // The way out renders even without a note: a slow, deleted or out-of-scope note must not leave a
  // grey page with nothing to click.
  const back = (
    <WorkspaceBack
      fallback={
        <Button asChild variant="outline">
          <Link to="/residents/$residentId/therapies" params={{ residentId }}>
            Back to the chart
          </Link>
        </Button>
      }
    />
  );

  if (note.isError)
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap gap-2">{back}</div>
        <ReadFailed
          message="Could not load this note."
          error={note.error}
          onRetry={() => void note.refetch()}
          retrying={note.isFetching}
        />
      </div>
    );
  if (note.isPending || !note.data)
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap gap-2">{back}</div>
        <Skeleton className="h-96 w-full" />
      </div>
    );

  const data = note.data;
  const facilityId = track?.facilityId ?? null;
  const canChart = can("therapy:chart", facilityId);
  const signer = { id: subject.userId, therapyJobRole: me.user.therapyJobRole };
  const shape = {
    id: data.id,
    noteType: data.noteType,
    status: data.status,
    durationMinutes: data.durationMinutes,
    supervisoryTherapistId: data.supervisoryTherapistId,
    episodeSupervisoryTherapistId: episode?.supervisoryTherapistId ?? null,
    chargeCount: data.charges.length,
    vitalsCount: data.vitals?.readings.length ?? 0,
  };
  const signRefusal = whyCannotSign(signer, shape);
  const cosignRefusal = whyCannotCosign(signer, {
    ...shape,
    assistantSignedById: data.assistantSignedById,
  });

  const sharedVisit = Boolean(data.supervisoryAssessment?.trim()) || data.supervisionDue;
  const supervisionDue = data.supervisionDue;
  const isDraft = data.status === "draft";
  const evaluation = PHYSICIAN_NOTE_TYPES.has(data.noteType);
  const isVisit = data.noteType !== "memo";
  // The physician is in the loop only for an evaluation or recertification on an OUTPATIENT
  // episode; the rule is `@alm/domain`'s and the API enforces it. Undecided until the track loads:
  // defaulting to outpatient would flash the loop onto a Home Health note.
  const physicianLoop =
    Boolean(episode) && therapyPhysicianSignatureRequired(data.noteType, episode?.careSetting);
  const waiting = requests.data?.find(
    (row) => row.entityId === noteId && row.status === "pending" && !row.cancelledAt,
  );
  // A note certified before the Home Health rule keeps its record and filed page.
  const physicianOnFile = Boolean(data.physicianSignedAt || data.physicianSignedDocPath);
  const signLabel = canChart && !signRefusal ? (isDraft ? "Sign" : "Re-sign") : null;
  // The rail reads the SAVED note: unsaved text is already shown by each section's Save button, and
  // a second live source could disagree.
  const sections = noteSections({
    showGoals: evaluation,
    isVisit,
    hasAudit: data.audits.length > 0,
    vitalsCount: data.vitals?.readings.length ?? 0,
    chargeCount: data.charges.length,
    signed: Boolean(data.signedAt ?? data.assistantSignedAt),
    values: data,
  });
  const panel = effectiveView(view, sections);

  return (
    <div className="space-y-6">
      <input
        ref={signedPageInput}
        type="file"
        className="hidden"
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          event.target.value = "";
          if (chosen) void actions.fileSigned(chosen);
        }}
      />
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {track ? label(THERAPY_DISCIPLINES, track.discipline) : "Therapy"},{" "}
            {label(THERAPY_NOTE_TYPES, data.noteType)}
            <Badge variant={statusTone(data.status)}>{statusLabel(data.status)}</Badge>
            {sharedVisit ? <Badge variant="secondary">Shared supervisory visit</Badge> : null}
          </span>
        }
        meta={episode?.title ? `Episode: ${episode.title}` : "Episode without a title"}
        actions={
          <NoteHeaderActions
            back={back}
            noteId={noteId}
            noteLabel={label(THERAPY_NOTE_TYPES, data.noteType)}
            data={data}
            canChart={canChart}
            canCosign={can("therapy:cosign", facilityId)}
            cosignRefusal={cosignRefusal}
            physicianLoop={physicianLoop}
            waitingId={waiting?.id}
            actions={actions}
            onCosign={() => setCosigning(true)}
            onPickFile={() => signedPageInput.current?.click()}
            onClearSigned={() => setClearingSigned(true)}
          />
        }
      />

      {signRefusal && canChart ? (
        <Alert>
          <AlertDescription>{signRefusal}</AlertDescription>
        </Alert>
      ) : null}

      <NotePanels
        data={data}
        noteId={noteId}
        panel={panel}
        sections={sections}
        onView={setView}
        discipline={track?.discipline ?? null}
        catalog={codes.data ?? []}
        evaluation={evaluation}
        canChart={canChart}
        isDraft={isDraft}
        isVisit={isVisit}
        signLabel={signLabel}
        sharedVisit={sharedVisit}
        physicianLoop={physicianLoop}
        physicianOnFile={physicianOnFile}
        pendingSince={waiting?.requestedAt ?? null}
        actions={actions}
        onSign={(values) => setSigning(values)}
      />

      {clearingSigned ? (
        <RemoveConfirm
          title="Remove the signed page?"
          confirmLabel="Remove"
          description="The note goes back to waiting on the physician's signature. The removal is kept in its audit trail."
          onConfirm={() => actions.clearSigned.mutate({ noteId })}
          onClose={() => setClearingSigned(false)}
        />
      ) : null}

      <SignDialog
        open={signing !== null}
        title={isDraft ? "Sign" : "Re-sign"}
        description="Your name, degree and the time are recorded with the signature. Wording you changed on this screen is signed with it, and the change is audited."
        assessmentLabel={null}
        assessmentDefault=""
        pending={actions.sign.isPending}
        onOpenChange={(open) => !open && setSigning(null)}
        onConfirm={(password) => {
          if (!signing) return;
          const { noteId: _noteId, ...content } = signing;
          actions.sign.mutate({ noteId, password, content });
        }}
      />

      <CosignDialog
        open={cosigning}
        assessmentLabel={supervisionDue || sharedVisit ? "Supervisory assessment" : null}
        assessmentDefault={data.supervisoryAssessment ?? ATTESTATION}
        needsUnit={
          supervisionDue &&
          data.timedUnits > 0 &&
          !data.charges.some((charge) => charge.performedById === subject.userId)
        }
        charges={data.charges}
        catalog={codes.data ?? []}
        pending={actions.cosign.isPending}
        onOpenChange={setCosigning}
        onConfirm={(password, assessment, unit) =>
          actions.cosign.mutate({ noteId, password, supervisoryAssessment: assessment, ...unit })
        }
      />
    </div>
  );
}
